import { useEffect, useState } from "react";
import { MessageCircle, Mail, Send, CheckCircle2, AlertCircle, Bell, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function AdminNotifications() {
    const [cfg, setCfg] = useState(null);
    const [testing, setTesting] = useState(false);
    const [triggering, setTriggering] = useState(false);

    const load = async () => {
        const { data } = await api.get("/admin/notifications-config");
        setCfg(data);
    };
    useEffect(() => { load(); }, []);

    const testWhatsApp = async () => {
        setTesting(true);
        try {
            await api.post("/admin/notifications/test-whatsapp");
            toast.success("Mensagem de teste enviada! Verifique o WhatsApp.");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setTesting(false); }
    };

    const triggerReminders = async () => {
        setTriggering(true);
        try {
            const { data } = await api.post("/admin/notifications/send-reminders-now");
            toast.success(`${data.sent} lembrete(s) enviado(s) para serviços de amanhã.`);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setTriggering(false); }
    };

    if (!cfg) return <div>A carregar...</div>;

    return (
        <div className="max-w-3xl space-y-6" data-testid="admin-notifications">
            <div>
                <h1 className="font-heading text-3xl font-bold tracking-tight">Notificações</h1>
                <p className="text-white/60">Configuração das notificações automáticas.</p>
            </div>

            {/* Email */}
            <div className="ab-card p-8">
                <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-md bg-[#C9A961]/10 border border-[#C9A961]/30 flex items-center justify-center flex-shrink-0">
                        <Mail className="w-5 h-5 text-[#C9A961]" />
                    </div>
                    <div className="flex-1">
                        <div className="flex items-center gap-2">
                            <h2 className="font-heading font-bold text-lg">Notificações por email</h2>
                            <span className="inline-flex items-center gap-1 text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">
                                <CheckCircle2 className="w-3 h-3" /> Ativo
                            </span>
                        </div>
                        <p className="text-sm text-white/60 mt-1">Emails automáticos a cada novo pedido — pronto a usar, sem configuração.</p>
                        <div className="mt-4 space-y-2 text-sm">
                            <div className="flex justify-between py-1.5 border-b border-[#2A2A2A]">
                                <span className="text-white/50">Notificações admin</span>
                                <span className="font-medium">{cfg.admin_notification_email}</span>
                            </div>
                            <div className="flex justify-between py-1.5">
                                <span className="text-white/50">Confirmação cliente</span>
                                <span className="font-medium">Automático após submissão</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* WhatsApp */}
            <div className="ab-card p-8">
                <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                        <MessageCircle className="w-5 h-5 text-[#25D366]" />
                    </div>
                    <div className="flex-1">
                        <div className="flex items-center gap-2">
                            <h2 className="font-heading font-bold text-lg">Notificações WhatsApp</h2>
                            {cfg.whatsapp_ready ? (
                                <span className="inline-flex items-center gap-1 text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">
                                    <CheckCircle2 className="w-3 h-3" /> Ativo
                                </span>
                            ) : (
                                <span className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                                    <AlertCircle className="w-3 h-3" /> Ativação necessária
                                </span>
                            )}
                        </div>
                        <p className="text-sm text-white/60 mt-1">
                            Recebe mensagem WhatsApp em <b>+{cfg.admin_whatsapp_number}</b> a cada novo pedido.
                        </p>

                        {!cfg.callmebot_configured && (
                            <div className="mt-4 p-4 rounded-lg bg-[#C9A961]/10 border border-[#C9A961]/30">
                                <div className="flex items-start gap-2">
                                    <AlertCircle className="w-4 h-4 text-[#C9A961] flex-shrink-0 mt-0.5" />
                                    <div className="text-sm text-[#C9A961]/90">
                                        <div className="font-semibold mb-2">Ativação em 3 passos (única vez, gratuito):</div>
                                        <ol className="list-decimal ml-4 space-y-1.5">
                                            <li>Adicione <b>+34 644 51 95 23</b> aos contactos do telemóvel (número do CallMeBot).</li>
                                            <li>Envie a mensagem exata <span className="font-mono bg-black px-1.5 py-0.5 rounded border">I allow callmebot to send me messages</span> desse telemóvel via WhatsApp.</li>
                                            <li>Espere pela resposta com a <b>APIKEY</b> — de seguida, coloque-a no ficheiro <span className="font-mono text-xs bg-black px-1.5 py-0.5 rounded border">/app/backend/.env</span> na linha <span className="font-mono text-xs">CALLMEBOT_API_KEY=&quot;...&quot;</span> e reinicie o backend.</li>
                                        </ol>
                                        <a href="https://www.callmebot.com/blog/free-api-whatsapp-messages/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#C9A961] hover:underline mt-3 text-xs font-medium">
                                            Instruções detalhadas <ExternalLink className="w-3 h-3" />
                                        </a>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="mt-4 flex gap-2">
                            <Button onClick={testWhatsApp} disabled={testing || !cfg.callmebot_configured} variant="outline" data-testid="test-whatsapp">
                                {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Enviar mensagem de teste
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Reminders */}
            <div className="ab-card p-8">
                <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                        <Bell className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                        <div className="flex items-center gap-2">
                            <h2 className="font-heading font-bold text-lg">Lembretes automáticos ao cliente</h2>
                            <span className="inline-flex items-center gap-1 text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">
                                <CheckCircle2 className="w-3 h-3" /> Ativo
                            </span>
                        </div>
                        <p className="text-sm text-white/60 mt-1">
                            Todos os dias às <b>09:00 (Europe/Lisbon)</b>, o sistema envia um lembrete por email a cada cliente com serviço agendado ou aceite para o dia seguinte.
                        </p>
                        <div className="mt-4">
                            <Button onClick={triggerReminders} disabled={triggering} variant="outline" data-testid="trigger-reminders">
                                {triggering ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Enviar lembretes agora (manual)
                            </Button>
                            <p className="text-xs text-white/50 mt-2">Útil para testar ou para forçar o envio imediato aos serviços marcados para amanhã.</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
